import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  BiListCheck,
  BiMinusCircle,
  BiMoney,
  BiSolidCheckCircle,
  BiSolidErrorCircle,
  BiSolidXCircle,
  BiTime,
} from 'react-icons/bi';
import type { IconType } from 'react-icons';
import { StatusChip } from '@/components/common/StatusChip';
import { toneForInstallmentStatus } from '@/lib/accountStatus';
import { formatAmountNumber, formatCurrencyEGP, formatDayLong, formatTimeOnly } from '@/lib/format';
import type { CalendarEvent, InstallmentStatus } from '@/types/shared';
import './CalendarEventChip.css';

/** Trailing status glyph on the compact payment chip — same shape map as `StatusChip`'s
 * tone->icon table (T104 §2.3), rendered at chip-text color (never tone-colored) so it stays
 * subtle next to the type color; the shape alone already satisfies "not color-alone". */
const STATUS_ICON: Record<InstallmentStatus, IconType> = {
  Pending: BiMinusCircle,
  Paid: BiSolidCheckCircle,
  Overdue: BiSolidXCircle,
  'Partially Paid': BiSolidErrorCircle,
};

interface CalendarEventChipProps {
  event: CalendarEvent;
  language: string;
  /** `compact` = grid-cell chip (abbreviated, density-capped); `expanded` = day-detail popover
   * row (full text, never truncated) — T104 §2.2. */
  variant: 'compact' | 'expanded';
}

/** One event chip — T104 §2.2. Always icon + text (never icon/color-alone), per-type color via
 * the `--color-calendar-*` tokens (T104 §2.1), and a full `aria-label` accessible name (§2.6)
 * since the visible text is intentionally abbreviated in the `compact` variant. */
export function CalendarEventChip({ event, language, variant }: CalendarEventChipProps) {
  const { t } = useTranslation();

  if (event.type === 'exam') {
    const dateLabel = formatDayLong(event.date, language);
    const sectionLabel = t(`examBookings.sections.${event.examSection}`);
    const accessibleName = t('calendar.chip.examAccessibleNameTemplate', {
      studentName: event.title,
      section: sectionLabel,
      date: dateLabel,
    });

    return (
      <Link
        to={event.deepLink}
        className="fa-cal-chip fa-cal-chip--exam"
        aria-label={accessibleName}
      >
        <BiListCheck className="fa-cal-chip__icon" aria-hidden="true" />
        <span className="fa-cal-chip__text">
          <bdi>{event.title}</bdi> — {sectionLabel}
        </span>
      </Link>
    );
  }

  if (event.type === 'meeting') {
    const dateLabel = formatDayLong(event.date, language);
    const timeLabel = formatTimeOnly(event.startsAt, language);
    const accessibleName = t('calendar.chip.meetingAccessibleNameTemplate', {
      leadName: event.title,
      time: timeLabel,
      date: dateLabel,
      agentName: event.assignedAgentName,
    });

    return (
      <span className="fa-cal-chip fa-cal-chip--meeting">
        <Link to={event.deepLink} className="fa-cal-chip__link" aria-label={accessibleName}>
          <BiTime className="fa-cal-chip__icon" aria-hidden="true" />
          <span className="fa-cal-chip__text">
            {variant === 'compact' ? (
              <>
                {timeLabel} <bdi>{event.title}</bdi>
              </>
            ) : (
              <>
                {timeLabel} — <bdi>{event.title}</bdi>
              </>
            )}
          </span>
        </Link>
        {/* Inert plain text — DEC-CAL2, never a link, any role. */}
        <span className="fa-cal-chip__agent">
          {variant === 'compact'
            ? `${t('calendar.chip.agentPrefix')} ${event.assignedAgentName}`
            : `(${t('calendar.chip.agentLabelTemplate', { agentName: event.assignedAgentName })})`}
        </span>
      </span>
    );
  }

  // payment
  const dateLabel = formatDayLong(event.date, language);
  const statusLabel = t(`accounts.detail.installments.status.${event.installmentStatus}`);
  const amountValue = Number(event.amount);
  const amountDisplay = formatCurrencyEGP(amountValue, language);
  const accessibleName = t('calendar.chip.paymentAccessibleNameTemplate', {
    studentName: event.title,
    // Pre-formatted (i18next's default interpolation just stringifies a raw number, no thousands
    // separator/decimals) — the template already appends its own "EGP"/"ج.م" word, so this is the
    // bare number portion only (`formatAmountNumber`), not the currency-suffixed display string.
    amount: formatAmountNumber(amountValue, language),
    status: statusLabel,
    date: dateLabel,
  });
  const StatusIcon = STATUS_ICON[event.installmentStatus];
  // 2026-08 — an overdue installment (past its due date and still unpaid) overrides the teal
  // payment-type color with the danger red, so a collector spots it without reading the chip. The
  // status icon/StatusChip stays regardless, so the cue is never color-alone (T104 §7.3) — the red
  // only raises the salience of a distinction that was already encoded in shape and text.
  const isOverdue = event.installmentStatus === 'Overdue';

  return (
    <Link
      to={event.deepLink}
      className={`fa-cal-chip fa-cal-chip--payment${isOverdue ? ' fa-cal-chip--payment-overdue' : ''}`}
      aria-label={accessibleName}
    >
      <BiMoney className="fa-cal-chip__icon" aria-hidden="true" />
      <span className="fa-cal-chip__text">
        <bdi>{event.title}</bdi> — <span className="fa-ltr-isolate">{amountDisplay}</span>
      </span>
      {variant === 'compact' ? (
        <StatusIcon className="fa-cal-chip__status-icon" aria-hidden="true" />
      ) : (
        <span className="fa-cal-chip__status-full">
          <StatusChip
            label={statusLabel}
            tone={toneForInstallmentStatus(event.installmentStatus)}
          />
        </span>
      )}
    </Link>
  );
}
